import type { Metadata } from "next"
import { notFound } from "next/navigation"
import { StaticPageIsolateContent } from "@/components/shared/static-pages/isolate-content"
import {
  getPublishedStaticPageSlugs,
  getStaticPageBySlug,
  getStaticPageRenderPayloadBySlug,
} from "@/services/static-page-service"

interface DynamicStaticPageProps {
  params: Promise<{ slug: string }>
}

const HTML_TAG_REGEX = /<[^>]*>/g

const stripHtml = (value: string): string => {
  return value.replace(HTML_TAG_REGEX, " ").replace(/\s+/g, " ").trim()
}

export async function generateStaticParams(): Promise<{ slug: string }[]> {
  const slugs = await getPublishedStaticPageSlugs()

  return slugs.map((slug) => ({ slug }))
}

export async function generateMetadata({
  params,
}: DynamicStaticPageProps): Promise<Metadata> {
  const { slug } = await params
  const page = await getStaticPageBySlug(slug)

  if (!page) {
    return {
      title: "Página não encontrada",
      robots: {
        index: false,
        follow: true,
      },
    }
  }

  const rawTitle = page.title?.rendered ?? ""
  const rawDescription = page.content?.rendered ?? ""

  return {
    title: stripHtml(rawTitle),
    description: stripHtml(rawDescription).slice(0, 160),
  }
}

export default async function DynamicStaticPage({
  params,
}: DynamicStaticPageProps): Promise<React.ReactNode> {
  const { slug } = await params
  const payload = await getStaticPageRenderPayloadBySlug(slug)

  if (!payload) {
    notFound()
  }

  const { mode, page, scripts, stylesheets, inlineStyles } = payload
  const pageHtml = payload.html ?? page.content?.rendered ?? ""
  const allowScriptExecution = mode === "full"

  return (
    <main className='w-full px-4 md:px-0'>
      <article className='wp-content mx-auto max-w-375 rounded-xl border border-zinc-200 bg-white p-6 md:p-10'>
        <StaticPageIsolateContent
          allowScriptExecution={allowScriptExecution}
          html={pageHtml}
          inlineStyles={inlineStyles}
          scripts={scripts}
          stylesheets={stylesheets}
        />
      </article>
    </main>
  )
}
